import React, { useEffect, useMemo, useState } from 'react';
import Datatable from '../../components/Datatable';
import FormDialog from '../../components/Datatable/FormDialog';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    IconButton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import api, { create, get_one, update, delete_one } from '../../services/api';
import EditIcon from '@mui/icons-material/Edit';
import { useNotify } from '../../components/Datatable/NotifyProvider';
import { useFormError } from '../../components/Datatable/FormErrorProvider';
import { RemoveCircleOutline } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../components/AuthProvider';
import Swal from 'sweetalert2';

// ---------------------------------------------------------------------------
// Catálogo de rótulos e domínios (linguagem do produto, não do backend)
// ---------------------------------------------------------------------------

const MODULE_LABELS = {
    user: 'Usuários',
    role: 'Cargos',
    access: 'Acessos',
    group: 'Grupos',
    nas: 'NAS',
    log_fail: 'Logs de Falha',
    log_session: 'Logs de Sessão',
    postauth: 'Pós-auth',
    dns_node: 'Nós DNS',
    dns_localzone: 'Zonas DNS',
    dns_acl: 'ACL DNS',
    dns_blacklist: 'Blacklist DNS',
    pegeia: 'Fontes Syslog',
};

const ACTION_LABELS = {
    get_all: 'Listar',
    get_by_id: 'Ver',
    create: 'Criar',
    update: 'Editar',
    delete: 'Excluir',
};

const ACTION_ORDER = ['get_all', 'get_by_id', 'create', 'update', 'delete'];

const DOMAINS = [
    { key: 'gate', label: 'Gate', modules: ['access', 'group', 'nas', 'log_fail', 'log_session', 'postauth'] },
    { key: 'dns', label: 'DNS', modules: ['dns_node', 'dns_localzone', 'dns_acl', 'dns_blacklist'] },
    { key: 'syslog', label: 'Syslog', modules: ['pegeia'] },
    { key: 'sistema', label: 'Sistema', modules: ['user', 'role'] },
];

const READ_ACTIONS = ['get_all', 'get_by_id'];
const EDITOR_ACTIONS = ['get_all', 'get_by_id', 'create', 'update']; // sem excluir
const FULL_ACTIONS = ['get_all', 'get_by_id', 'create', 'update', 'delete'];

// ---------------------------------------------------------------------------
// Helpers de catálogo
// ---------------------------------------------------------------------------

function parsePerm(description) {
    const lastDot = description.lastIndexOf('.');
    if (lastDot < 0) return { module: description, action: '' };
    return {
        module: description.slice(0, lastDot),
        action: description.slice(lastDot + 1),
    };
}

function moduleLabel(moduleKey) {
    if (MODULE_LABELS[moduleKey]) return MODULE_LABELS[moduleKey];
    return moduleKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// permissions: [{id, description}, ...]
// returns: { byModuleAction, allModules, allIds, modulesPresentByDomain }
function buildIndex(permissions) {
    const byModuleAction = {};
    const allIds = new Set();
    for (const p of permissions) {
        const { module, action } = parsePerm(p.description);
        if (!byModuleAction[module]) byModuleAction[module] = {};
        byModuleAction[module][action] = p;
        allIds.add(p.id);
    }
    const knownModules = new Set(DOMAINS.flatMap((d) => d.modules));
    const allModules = Object.keys(byModuleAction);
    const orphanModules = allModules.filter((m) => !knownModules.has(m));
    const modulesPresentByDomain = {};
    for (const d of DOMAINS) {
        modulesPresentByDomain[d.key] = d.modules.filter((m) => byModuleAction[m]);
    }
    if (orphanModules.length > 0) {
        modulesPresentByDomain.outros = orphanModules;
    }
    return { byModuleAction, allModules, allIds, modulesPresentByDomain, orphanModules };
}

function idsForModuleActions(byModuleAction, modules, actions) {
    const ids = [];
    for (const m of modules) {
        const actionsMap = byModuleAction[m] || {};
        for (const a of actions) {
            if (actionsMap[a]) ids.push(actionsMap[a].id);
        }
    }
    return ids;
}

function idsForModulesAllActions(byModuleAction, modules) {
    const ids = [];
    for (const m of modules) {
        const actionsMap = byModuleAction[m] || {};
        for (const a of Object.keys(actionsMap)) {
            ids.push(actionsMap[a].id);
        }
    }
    return ids;
}

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

// Domain presets: cada um devolve Set<id> para o domínio inteiro.
// Apply = SUBSTITUI a parte daquele domínio na seleção (não é aditivo).
// Global presets: devolvem Set<id> sobre TODO o catálogo.
// Apply = SUBSTITUI a seleção inteira.
const DOMAIN_MODULES = {
    gate: ['access', 'group', 'nas', 'log_fail', 'log_session', 'postauth'],
    dns: ['dns_node', 'dns_localzone', 'dns_acl', 'dns_blacklist'],
    syslog: ['pegeia'],
    sistema: ['user', 'role'],
};

const PRESETS = {
    domain: {
        gate: {
            Leitura: (idx) => new Set(idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.gate, READ_ACTIONS)),
            Editor: (idx) => new Set(idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.gate, EDITOR_ACTIONS)),
            Tudo: (idx) => new Set(idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.gate, FULL_ACTIONS)),
        },
        dns: {
            Leitura: (idx) => new Set(idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.dns, READ_ACTIONS)),
            Editor: (idx) => new Set(idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.dns, EDITOR_ACTIONS)),
            Tudo: (idx) => new Set(idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.dns, FULL_ACTIONS)),
        },
        syslog: {
            Leitura: (idx) => new Set(idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.syslog, READ_ACTIONS)),
            Editor: (idx) => new Set(idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.syslog, EDITOR_ACTIONS)),
            Tudo: (idx) => new Set(idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.syslog, FULL_ACTIONS)),
        },
        sistema: {
            Leitura: (idx) => new Set(idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.sistema, READ_ACTIONS)),
            Tudo: (idx) => new Set(idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.sistema, FULL_ACTIONS)),
        },
    },
    global: {
        // Leitura: vê tudo, não edita nada.
        Leitura: (idx) =>
            new Set(idsForModuleActions(
                idx.byModuleAction,
                [...DOMAIN_MODULES.gate, ...DOMAIN_MODULES.dns, ...DOMAIN_MODULES.syslog, ...DOMAIN_MODULES.sistema],
                READ_ACTIONS,
            )),
        // Editor: edita Gate/DNS/Syslog (CRUD completo) + lê Sistema (vê usuários e cargos, mas não mexe).
        Editor: (idx) =>
            new Set([
                ...idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.gate, FULL_ACTIONS),
                ...idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.dns, FULL_ACTIONS),
                ...idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.syslog, FULL_ACTIONS),
                ...idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.sistema, READ_ACTIONS),
            ]),
        // Admin: Editor + gerência completa de usuários (criar/editar/excluir). Cargos só leitura.
        Admin: (idx) =>
            new Set([
                ...idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.gate, FULL_ACTIONS),
                ...idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.dns, FULL_ACTIONS),
                ...idsForModuleActions(idx.byModuleAction, DOMAIN_MODULES.syslog, FULL_ACTIONS),
                ...idsForModuleActions(idx.byModuleAction, ['user'], FULL_ACTIONS),
                ...idsForModuleActions(idx.byModuleAction, ['role'], READ_ACTIONS),
            ]),
    },
};

// ---------------------------------------------------------------------------
// Componente raiz
// ---------------------------------------------------------------------------

export default function Role() {
    const auth = useAuth();
    const [formState, setFormState] = useState({ open: false, id: null });
    const [refresh, setRefresh] = useState(false);
    useEffect(() => {
        document.title = 'IPV7 Tools - Config Cargos';
    }, []);
    return (
        <>
            <FormItem
                open={formState.open}
                id={formState.id}
                handlerClose={() => setFormState({ open: false, id: null })}
                handlerSubmit={() => setRefresh(true)}
                btnSubmit={formState.id ? 'Atualizar' : 'Salvar'}
                btnClose={'Fechar'}
            />
            <Datatable
                refresh={refresh}
                handlerRefresh={() => setRefresh(!refresh)}
                title="Cargos"
                endpoint="/role/"
                options={{
                    customToolbar: () => (
                        <>
                            <Tooltip title="Remover todos os filtros">
                                <IconButton
                                    onClick={() => {
                                        localStorage.removeItem('alarms');
                                        location.reload();
                                    }}
                                >
                                    <RemoveCircleOutline />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Adicionar">
                                <IconButton
                                    disabled={!auth.hasPerm('role.create')}
                                    onClick={() => setFormState({ open: true, id: null })}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    ),
                    customToolbarSelect: () => '',
                }}
                columns={[
                    { name: 'id', label: 'ID', options: { display: false } },
                    { name: 'description', label: 'Descrição' },
                    {
                        name: 'actions',
                        label: 'Ações',
                        options: {
                            filter: false,
                            sort: false,
                            customBodyRender: (value, tableMeta) => (
                                <Tooltip title="Editar">
                                    <IconButton
                                        disabled={!auth.hasPerm('role.update')}
                                        onClick={() => setFormState({ open: true, id: tableMeta.rowData[0] })}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                            ),
                        },
                    },
                ]}
            />
        </>
    );
}

// ---------------------------------------------------------------------------
// FormItem — diálogo de edição/criação
// ---------------------------------------------------------------------------

function FormItem({ id, open, handlerClose, handlerSubmit, btnClose = 'Fechar', btnSubmit = 'Salvar' }) {
    const notify = useNotify();
    const error = useFormError();
    const auth = useAuth();
    const [allPermissions, setAllPermissions] = useState([]);
    const [description, setDescription] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [activeDomain, setActiveDomain] = useState(DOMAINS[0].key);
    const [isLoaded, setIsLoaded] = useState(true);

    const index = useMemo(() => buildIndex(allPermissions), [allPermissions]);
    const domains = useMemo(() => {
        const list = DOMAINS.map((d) => ({
            ...d,
            modulesPresent: index.modulesPresentByDomain[d.key] || [],
        })).filter((d) => d.modulesPresent.length > 0);
        if (index.orphanModules && index.orphanModules.length > 0) {
            list.push({ key: 'outros', label: 'Outros', modules: index.orphanModules, modulesPresent: index.orphanModules });
        }
        return list;
    }, [index]);

    useEffect(() => {
        if (!open) return;
        api.get('role/permission/')
            .then((res) => setAllPermissions(res.data || []))
            .catch(() => setAllPermissions([]));
    }, [open]);

    useEffect(() => {
        if (!open) return;
        error.setErrors([]);
        if (id) {
            setIsLoaded(false);
            get_one('/role', id)
                .then((res) => {
                    setDescription(res.data.description || '');
                    const ids = (res.data.permissions || []).map((p) => p.id);
                    setSelectedIds(new Set(ids));
                    setIsLoaded(true);
                })
                .catch((e) => {
                    console.log(e);
                    setIsLoaded(true);
                });
        } else {
            setDescription('');
            setSelectedIds(new Set());
        }
    }, [id, open]);

    useEffect(() => {
        if (domains.length > 0 && !domains.find((d) => d.key === activeDomain)) {
            setActiveDomain(domains[0].key);
        }
    }, [domains, activeDomain]);

    // ----- Toggles ---------------------------------------------------------

    const toggleOne = (permId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(permId)) next.delete(permId);
            else next.add(permId);
            return next;
        });
    };

    const toggleModule = (moduleKey) => {
        const actionsMap = index.byModuleAction[moduleKey] || {};
        const ids = Object.values(actionsMap).map((p) => p.id);
        const allChecked = ids.every((i) => selectedIds.has(i));
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allChecked) ids.forEach((i) => next.delete(i));
            else ids.forEach((i) => next.add(i));
            return next;
        });
    };

    const toggleActionInDomain = (domainKey, action) => {
        const dom = domains.find((d) => d.key === domainKey);
        if (!dom) return;
        const ids = [];
        for (const m of dom.modulesPresent) {
            const actionsMap = index.byModuleAction[m] || {};
            if (actionsMap[action]) ids.push(actionsMap[action].id);
        }
        if (ids.length === 0) return;
        const allChecked = ids.every((i) => selectedIds.has(i));
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allChecked) ids.forEach((i) => next.delete(i));
            else ids.forEach((i) => next.add(i));
            return next;
        });
    };

    const applyDomainPreset = (domainKey, presetName) => {
        const fn = PRESETS.domain[domainKey] && PRESETS.domain[domainKey][presetName];
        if (!fn) return;
        const dom = domains.find((d) => d.key === domainKey);
        if (!dom) return;
        const domainIds = new Set(idsForModulesAllActions(index.byModuleAction, dom.modulesPresent));
        const presetIds = fn(index);
        setSelectedIds((prev) => {
            const next = new Set();
            for (const i of prev) if (!domainIds.has(i)) next.add(i);
            for (const i of presetIds) next.add(i);
            return next;
        });
    };

    const clearDomain = (domainKey) => {
        const dom = domains.find((d) => d.key === domainKey);
        if (!dom) return;
        const ids = idsForModulesAllActions(index.byModuleAction, dom.modulesPresent);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            ids.forEach((i) => next.delete(i));
            return next;
        });
    };

    const applyGlobalPreset = (presetName) => {
        const fn = PRESETS.global[presetName];
        if (!fn) return;
        setSelectedIds(fn(index));
    };

    // ----- Estado derivado -------------------------------------------------

    const moduleSelectionState = (moduleKey) => {
        const actionsMap = index.byModuleAction[moduleKey] || {};
        const ids = Object.values(actionsMap).map((p) => p.id);
        if (ids.length === 0) return 'none';
        const checked = ids.filter((i) => selectedIds.has(i)).length;
        if (checked === 0) return 'none';
        if (checked === ids.length) return 'all';
        return 'some';
    };

    const actionInDomainSelectionState = (domainKey, action) => {
        const dom = domains.find((d) => d.key === domainKey);
        if (!dom) return 'none';
        const ids = [];
        for (const m of dom.modulesPresent) {
            const actionsMap = index.byModuleAction[m] || {};
            if (actionsMap[action]) ids.push(actionsMap[action].id);
        }
        if (ids.length === 0) return 'none';
        const checked = ids.filter((i) => selectedIds.has(i)).length;
        if (checked === 0) return 'none';
        if (checked === ids.length) return 'all';
        return 'some';
    };

    const domainCounts = useMemo(() => {
        const out = {};
        for (const d of domains) {
            const ids = idsForModulesAllActions(index.byModuleAction, d.modulesPresent);
            const checked = ids.filter((i) => selectedIds.has(i)).length;
            out[d.key] = { checked, total: ids.length };
        }
        return out;
    }, [domains, index, selectedIds]);

    // ----- Submit ----------------------------------------------------------

    const wrapperSubmit = () => {
        const payload = {
            description,
            permissions: Array.from(selectedIds),
        };
        const onResponse = (res) => {
            if (res && res.data && res.data.errors) {
                error.setErrors(res.data.errors);
                return;
            }
            handlerClose();
            notify.setNotify({
                open: true,
                message: id ? 'Cargo atualizado com sucesso' : 'Cargo criado com sucesso.',
                severity: 'success',
            });
            error.setErrors([]);
            handlerSubmit();
        };
        const onError = (e) =>
            notify.setNotify({ open: true, message: e.message, severity: 'error' });

        if (id) {
            update('/role', id, payload).then(onResponse).catch(onError);
        } else {
            create('/role/create', payload).then(onResponse).catch(onError);
        }
    };

    // ----- Render ----------------------------------------------------------

    const activeDomainObj = domains.find((d) => d.key === activeDomain) || domains[0];
    const presetsForActive = activeDomainObj ? PRESETS.domain[activeDomainObj.key] || {} : {};

    return (
        <Box component="form" onSubmit={wrapperSubmit} sx={{ m: 2 }} display="flex" flexWrap="wrap">
            <FormDialog
                isLoaded={isLoaded}
                maxWidth="md"
                title={
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6" component="div">
                            {id ? 'Editar' : 'Adicionar'} Cargo
                        </Typography>
                        <IconButton
                            disabled={!auth.hasPerm('role.delete')}
                            sx={{ display: id ? 'block' : 'none' }}
                            onClick={() => {
                                Swal.fire({
                                    title: 'Excluir cargo?',
                                    text: 'Esta ação remove o cargo. Usuários vinculados podem ficar sem permissões.',
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonText: 'Sim, excluir',
                                    cancelButtonText: 'Cancelar',
                                    confirmButtonColor: '#e01b29',
                                }).then((result) => {
                                    if (!result.isConfirmed) return;
                                    delete_one('/role', id).then(() => {
                                        notify.setNotify({
                                            open: true,
                                            message: 'Cargo deletado com sucesso.',
                                            severity: 'success',
                                        });
                                        handlerClose();
                                        handlerSubmit();
                                    });
                                });
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                }
                open={open}
                handlerSubmit={wrapperSubmit}
                handlerClose={handlerClose}
                btnSubmit={btnSubmit}
                btnClose={btnClose}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                        sx={{ mb: 2, mt: 1 }}
                        error={error.verify('description')}
                        helperText={error.msgError('description')}
                        label="Descrição"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />

                    {domains.length === 0 ? (
                        <Typography variant="body2" sx={{ p: 2 }}>
                            Carregando permissões…
                        </Typography>
                    ) : (
                        <>
                            <Tabs
                                value={activeDomain}
                                onChange={(_, v) => setActiveDomain(v)}
                                variant="scrollable"
                                sx={{ borderBottom: 1, borderColor: 'divider' }}
                            >
                                {domains.map((d) => {
                                    const c = domainCounts[d.key] || { checked: 0, total: 0 };
                                    return (
                                        <Tab
                                            key={d.key}
                                            value={d.key}
                                            label={
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <span>{d.label}</span>
                                                    <Chip
                                                        size="small"
                                                        label={`${c.checked}/${c.total}`}
                                                        color={c.checked === 0 ? 'default' : c.checked === c.total ? 'primary' : 'warning'}
                                                    />
                                                </Stack>
                                            }
                                        />
                                    );
                                })}
                            </Tabs>

                            {activeDomainObj && (
                                <Box sx={{ pt: 2 }}>
                                    {Object.keys(presetsForActive).length > 0 && (
                                        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                                            <Typography variant="caption" sx={{ alignSelf: 'center', mr: 1 }}>
                                                Presets:
                                            </Typography>
                                            {Object.keys(presetsForActive).map((name) => (
                                                <Button
                                                    key={name}
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => applyDomainPreset(activeDomainObj.key, name)}
                                                >
                                                    {name}
                                                </Button>
                                            ))}
                                            <Button
                                                size="small"
                                                variant="text"
                                                color="error"
                                                onClick={() => clearDomain(activeDomainObj.key)}
                                            >
                                                Limpar
                                            </Button>
                                        </Stack>
                                    )}

                                    <PermissionMatrix
                                        domain={activeDomainObj}
                                        index={index}
                                        selectedIds={selectedIds}
                                        toggleOne={toggleOne}
                                        toggleModule={toggleModule}
                                        toggleActionInDomain={toggleActionInDomain}
                                        moduleSelectionState={moduleSelectionState}
                                        actionInDomainSelectionState={actionInDomainSelectionState}
                                    />
                                </Box>
                            )}

                            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                                    <Typography variant="caption" sx={{ alignSelf: 'center', mr: 1 }}>
                                        Presets globais:
                                    </Typography>
                                    {Object.keys(PRESETS.global).map((name) => (
                                        <Button
                                            key={name}
                                            size="small"
                                            variant="contained"
                                            color={name === 'Administrador' ? 'primary' : 'inherit'}
                                            onClick={() => applyGlobalPreset(name)}
                                        >
                                            {name}
                                        </Button>
                                    ))}
                                </Stack>
                            </Box>
                        </>
                    )}
                </Box>
            </FormDialog>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// PermissionMatrix — tabela de checkboxes para o domínio ativo
// ---------------------------------------------------------------------------

function PermissionMatrix({
    domain,
    index,
    selectedIds,
    toggleOne,
    toggleModule,
    toggleActionInDomain,
    moduleSelectionState,
    actionInDomainSelectionState,
}) {
    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Módulo</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                            Tudo
                        </TableCell>
                        {ACTION_ORDER.map((a) => (
                            <TableCell key={a} align="center" sx={{ fontWeight: 600 }}>
                                {ACTION_LABELS[a] || a}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {domain.modulesPresent.map((m) => {
                        const actionsMap = index.byModuleAction[m] || {};
                        const state = moduleSelectionState(m);
                        return (
                            <TableRow key={m} hover>
                                <TableCell>{moduleLabel(m)}</TableCell>
                                <TableCell align="center">
                                    <Checkbox
                                        size="small"
                                        checked={state === 'all'}
                                        indeterminate={state === 'some'}
                                        onChange={() => toggleModule(m)}
                                    />
                                </TableCell>
                                {ACTION_ORDER.map((a) => {
                                    const perm = actionsMap[a];
                                    if (!perm) {
                                        return (
                                            <TableCell key={a} align="center" sx={{ color: 'text.disabled' }}>
                                                —
                                            </TableCell>
                                        );
                                    }
                                    return (
                                        <TableCell key={a} align="center">
                                            <Checkbox
                                                size="small"
                                                checked={selectedIds.has(perm.id)}
                                                onChange={() => toggleOne(perm.id)}
                                            />
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 600 }}>TODOS o {domain.label}</TableCell>
                        <TableCell />
                        {ACTION_ORDER.map((a) => {
                            const state = actionInDomainSelectionState(domain.key, a);
                            if (state === 'none' && !domain.modulesPresent.some((m) => (index.byModuleAction[m] || {})[a])) {
                                return (
                                    <TableCell key={a} align="center" sx={{ color: 'text.disabled' }}>
                                        —
                                    </TableCell>
                                );
                            }
                            return (
                                <TableCell key={a} align="center">
                                    <Checkbox
                                        size="small"
                                        checked={state === 'all'}
                                        indeterminate={state === 'some'}
                                        onChange={() => toggleActionInDomain(domain.key, a)}
                                    />
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}
